import { useCallback, useEffect, useRef, useState } from 'react';
import { useMutation, useQuery } from 'react-query';
import { Alert, Autocomplete, Button, Checkbox, Divider, FormControlLabel, FormGroup, IconButton, LinearProgress, Modal, Stack, TextField, Tooltip, Typography } from '@mui/material';
import ButtonLoading from '@mui/lab/LoadingButton';
import HighlightOffRoundedIcon from '@mui/icons-material/HighlightOffRounded';
import NewReleasesIcon from '@mui/icons-material/NewReleases';
import HistoryOutlinedIcon from '@mui/icons-material/HistoryOutlined';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import { BudgetGenerationHistory } from './BudgetGenerationHistory';
import axios from 'libs/Axios';

// Match the existing cube Decision Point and input modal layout.
const modalContentStyle = {
  position: 'absolute' as const,
  top: '50%',
  left: '50%',
  transform: 'translate(-50%, -50%)',
  width: 560,
  maxWidth: '95vw',
  maxHeight: '90vh',
  overflowY: 'auto',
  boxSizing: 'border-box',
  bgcolor: 'background.paper',
  borderRadius: 1,
  px: 3,
  py: 2,
  outline: 'none',
};

const sectionBoxStyle = {
  bgcolor: '#FBFBFB',
  p: 1.5,
  borderRadius: 1,
  border: '1px solid #E3EBF6',
};

const filterAutoCompleteStyle = {
  '& .MuiOutlinedInput-root': { padding: '1px 2px' },
};

type Layer = { id: string; name: string; type: string };
type Operation = {
  operation_id: string; status: string; authorised_by: string; authorised_by_name?: string; requested_at: string;
  completed_at: string | null; source_layer_id: string; destination_layer_ids: string[];
  error_message: string | null;
};
type Info = { cubeName: string; canGenerate: boolean; layers: Layer[]; operation: Operation | null };

export type BudgetGenerationStatus =
  | 'STARTING'
  | 'IN_QUEUE'
  | 'IN_PROGRESS'
  | 'FINALIZING'
  | 'COMPLETED'
  | 'FAILED'
  | 'START_FAILED'
  | 'START_UNCONFIRMED'
  | 'STATUS_UNAVAILABLE'
  | 'REFRESH_UNAVAILABLE'
  | 'DRAFT_CONFLICT';

type ActiveBudgetGenerationStatus = 'IN_QUEUE' | 'IN_PROGRESS';

const isRunningGenerationStatus = (status?: string): status is ActiveBudgetGenerationStatus =>
  status === 'IN_QUEUE' || status === 'IN_PROGRESS';

export const isBudgetGenerationActive = (status: BudgetGenerationStatus | null) =>
  status === 'STARTING' || status === 'IN_QUEUE' || status === 'IN_PROGRESS' || status === 'FINALIZING'
  || status === 'START_UNCONFIRMED' || status === 'STATUS_UNAVAILABLE'
  || status === 'REFRESH_UNAVAILABLE' || status === 'DRAFT_CONFLICT';

const generationStatusMessage: Record<BudgetGenerationStatus, string> = {
  STARTING: 'Starting Budget Control Framework generation…',
  IN_QUEUE: 'Budget Control Framework generation is queued.',
  IN_PROGRESS: 'Generating Budget Control Framework…',
  FINALIZING: 'Budget Control Framework generated. Loading the latest cube values…',
  COMPLETED: 'Budget Control Framework generated successfully.',
  FAILED: 'Generation failed. Destination values were not replaced.',
  START_FAILED: 'Budget Control Framework generation did not start.',
  START_UNCONFIRMED: 'The generation start result is unclear. Checking the latest status…',
  STATUS_UNAVAILABLE: 'Could not load the latest generation status. Retrying automatically…',
  REFRESH_UNAVAILABLE: 'Budget Control Framework generated, but the latest cube values could not be loaded. Retrying automatically…',
  DRAFT_CONFLICT: 'Generated values are ready, but this cube has unsaved edits.',
};

const generationStatusSeverity: Record<BudgetGenerationStatus, 'info' | 'success' | 'warning' | 'error'> = {
  STARTING: 'info',
  IN_QUEUE: 'info',
  IN_PROGRESS: 'info',
  FINALIZING: 'info',
  COMPLETED: 'success',
  FAILED: 'error',
  START_FAILED: 'error',
  START_UNCONFIRMED: 'warning',
  STATUS_UNAVAILABLE: 'warning',
  REFRESH_UNAVAILABLE: 'warning',
  DRAFT_CONFLICT: 'warning',
};

const isRefreshError = (result: unknown) =>
  !!result && typeof result === 'object' && 'isError' in result
  && (result as { isError?: boolean }).isError === true;

const isRefreshBlockedByDraft = (result: unknown) =>
  !!result && typeof result === 'object' && 'blockedByUnsavedChanges' in result
  && (result as { blockedByUnsavedChanges?: boolean }).blockedByUnsavedChanges === true;

const isAmbiguousStartError = (error: unknown) => {
  const status = (error as { response?: { status?: number } } | null)?.response?.status;
  return !status || status === 408 || status >= 500;
};

export function BudgetGenerationProgress({
  status,
  onDismiss,
  onDiscardChanges,
}: {
  status: BudgetGenerationStatus;
  onDismiss?: () => void;
  onDiscardChanges?: () => void;
}) {
  const active = isBudgetGenerationActive(status);
  const waitingForDraftDecision = status === 'DRAFT_CONFLICT';
  return (
    <Alert
      severity={generationStatusSeverity[status]}
      role={active ? 'status' : 'alert'}
      aria-live={active ? 'polite' : 'assertive'}
      aria-atomic='true'
      action={
        waitingForDraftDecision && onDiscardChanges ? (
          <Button color='inherit' size='small' onClick={onDiscardChanges}>
            Discard edits &amp; reload
          </Button>
        ) : !active && onDismiss ? (
          <IconButton size='small' aria-label='Dismiss Budget Control Framework status' onClick={onDismiss}>
            <HighlightOffRoundedIcon fontSize='small' />
          </IconButton>
        ) : undefined
      }
      sx={{
        mx: 3.5,
        mt: 1.5,
        mb: 1.5,
        alignItems: 'center',
        '& .MuiAlert-message': { width: '100%' },
      }}
    >
      <Stack spacing={active ? 1 : 0}>
        <Typography variant='body2' fontWeight={700}>
          {generationStatusMessage[status]}
        </Typography>
        {active && (
          <>
            <Typography variant='caption' sx={{ color: '#667085' }}>
              {waitingForDraftDecision
                ? 'Your edits are preserved on this page. Discard them to load the generated values; you can still navigate elsewhere.'
                : 'You can continue viewing the cube or navigate elsewhere. Editing will resume when the latest values are loaded.'}
            </Typography>
            {!waitingForDraftDecision && (
              <LinearProgress
                aria-label='Budget Control Framework generation progress'
                sx={{
                  bgcolor: 'rgba(66, 187, 147, 0.18)',
                  '& .MuiLinearProgress-bar': { bgcolor: '#42BB93' },
                }}
              />
            )}
          </>
        )}
      </Stack>
    </Alert>
  );
}

interface BudgetSnapshotPanelProps {
  cubeId: string;
  onSaved: (operationId: string) => unknown | Promise<unknown>;
  onGenerationStatusChange?: (
    status: BudgetGenerationStatus | null,
    retryRefresh?: () => void,
    operationId?: string | null,
  ) => void;
  disabled?: boolean;
  disabledReason?: string;
}

export function BudgetSnapshotPanel({
  cubeId,
  onSaved,
  onGenerationStatusChange,
  disabled = false,
  disabledReason,
}: BudgetSnapshotPanelProps) {
  const url = `/api/v1/tenant/cube/${cubeId}/budget-control`;
  const [open, setOpen] = useState(false);
  const [showHistory, setShowHistory] = useState(false);
  const [source, setSource] = useState('');
  const [selectedDestinations, setSelectedDestinations] = useState<string[]>([]);
  const [operationId, setOperationId] = useState<string | null>(null);
  const [finalizedOperationId, setFinalizedOperationId] = useState<string | null>(null);
  const [cubeRefreshFailed, setCubeRefreshFailed] = useState(false);
  const [cubeRefreshBlocked, setCubeRefreshBlocked] = useState(false);
  const [cubeRefreshAttempt, setCubeRefreshAttempt] = useState(0);
  const [reconcilingStart, setReconcilingStart] = useState(false);
  const lastInfoOperationIdRef = useRef<string | null | undefined>(undefined);
  const generationStartBaselineOperationIdRef = useRef<string | null>(null);
  const generationStartInfoUpdatedAtRef = useRef(0);
  const reconciliationChecksRef = useRef(0);
  const onSavedRef = useRef(onSaved);
  const onGenerationStatusChangeRef = useRef(onGenerationStatusChange);
  onSavedRef.current = onSaved;
  onGenerationStatusChangeRef.current = onGenerationStatusChange;
  const info = useQuery(['budget-control', cubeId], async () => (await axios.get<Info>(url)).data, {
    refetchInterval: data => reconcilingStart || isRunningGenerationStatus(data?.operation?.status) ? 2000 : 5000,
  });
  const operation = useQuery(['budget-control-operation', cubeId, operationId],
    async () => (await axios.get<Operation>(`${url}/operations/${operationId}`)).data,
    { enabled: !!operationId, refetchInterval: data => ['COMPLETED', 'FAILED'].includes(data?.status || '') ? false : 2000 });
  useEffect(() => {
    if (!info.isSuccess) return;
    const latestOperation = info.data?.operation;
    const latestOperationId = latestOperation?.operation_id || null;
    const previousOperationId = lastInfoOperationIdRef.current;
    const isFirstInfoResponse = previousOperationId === undefined;
    lastInfoOperationIdRef.current = latestOperationId;

    if (latestOperation && (isRunningGenerationStatus(latestOperation.status)
      || (!isFirstInfoResponse && latestOperationId !== previousOperationId))) {
      setOperationId(latestOperationId);
    }

    if (!reconcilingStart || info.dataUpdatedAt <= generationStartInfoUpdatedAtRef.current) return;
    if (latestOperation && latestOperationId !== generationStartBaselineOperationIdRef.current) {
      setReconcilingStart(false);
      reconciliationChecksRef.current = 0;
      setOperationId(latestOperationId);
      return;
    }
    reconciliationChecksRef.current += 1;
    if (reconciliationChecksRef.current >= 2) {
      setReconcilingStart(false);
      reconciliationChecksRef.current = 0;
    }
  }, [info.data?.operation, info.dataUpdatedAt, info.isSuccess, reconcilingStart]);
  const generate = useMutation(async () => (await axios.post<{ operationId: string }>(`${url}/generate`, {
    sourceLayerId: source, destinationLayerIds: selectedDestinations, confirmReplacement: true,
  })).data, {
    onSuccess: data => {
      setReconcilingStart(false);
      setOperationId(data.operationId);
      info.refetch();
    },
    onError: error => {
      if (isAmbiguousStartError(error)) {
        reconciliationChecksRef.current = 0;
        setReconcilingStart(true);
      }
      info.refetch();
    },
  });
  const layers = info.data?.layers || [];
  const destinations = layers.filter(l => l.type === 'BUDGET' || l.type === 'GENERAL');
  const canSelectDestination = (layer: Layer, sourceId: string) =>
    layer.id !== sourceId && !(layer.type === 'BUDGET' && layers.find(l => l.id === sourceId)?.type === 'BUDGET');
  const currentOperation = operation.data || info.data?.operation;
  const currentInfoOperation = info.data?.operation;
  const fallbackStatus = currentInfoOperation?.operation_id === operationId ? currentInfoOperation.status : undefined;
  const status = operationId ? operation.data?.status || fallbackStatus : currentInfoOperation?.status;
  let polledGenerationStatus: BudgetGenerationStatus | null = null;
  if (operationId && operation.isError) {
    polledGenerationStatus = fallbackStatus === 'COMPLETED' || fallbackStatus === 'FAILED'
      ? fallbackStatus
      : 'STATUS_UNAVAILABLE';
  } else if (operationId && !status) {
    polledGenerationStatus = 'IN_QUEUE';
  } else if (isRunningGenerationStatus(status)) {
    polledGenerationStatus = status;
  } else if (operationId && (status === 'COMPLETED' || status === 'FAILED')) {
    polledGenerationStatus = status;
  }
  const requiresCubeRefresh = !!operationId && polledGenerationStatus === 'COMPLETED'
    && finalizedOperationId !== operationId;
  const generationStatus: BudgetGenerationStatus | null = operationId
    ? requiresCubeRefresh
      ? cubeRefreshBlocked
        ? 'DRAFT_CONFLICT'
        : cubeRefreshFailed
          ? 'REFRESH_UNAVAILABLE'
          : 'FINALIZING'
      : polledGenerationStatus
    : generate.isLoading
      ? 'STARTING'
      : reconcilingStart
        ? 'START_UNCONFIRMED'
        : generate.isError
        ? 'START_FAILED'
        : null;
  const operationBusy = isBudgetGenerationActive(generationStatus);
  const busy = generate.isLoading || operationBusy;
  useEffect(() => {
    if (!requiresCubeRefresh || !operationId) return;
    let cancelled = false;
    const refreshCompletedGeneration = async () => {
      setCubeRefreshFailed(false);
      let refreshSucceeded = false;
      try {
        const result = await onSavedRef.current(operationId);
        if (isRefreshBlockedByDraft(result)) {
          if (!cancelled) setCubeRefreshBlocked(true);
          return;
        }
        refreshSucceeded = !isRefreshError(result);
      } catch {
        refreshSucceeded = false;
      }
      void info.refetch();
      if (cancelled) return;
      if (refreshSucceeded) {
        setFinalizedOperationId(operationId);
      } else {
        setCubeRefreshFailed(true);
      }
    };
    void refreshCompletedGeneration();
    return () => { cancelled = true; };
    // Refresh the cube successfully before re-enabling editing.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cubeRefreshAttempt, operationId, requiresCubeRefresh]);
  useEffect(() => {
    if (!requiresCubeRefresh || !cubeRefreshFailed) return;
    const retry = window.setTimeout(() => setCubeRefreshAttempt(attempt => attempt + 1), 5000);
    return () => window.clearTimeout(retry);
  }, [cubeRefreshFailed, requiresCubeRefresh]);
  const retryCubeRefresh = useCallback(() => {
    setCubeRefreshBlocked(false);
    setCubeRefreshAttempt(attempt => attempt + 1);
  }, []);
  useEffect(() => {
    setCubeRefreshFailed(false);
    setCubeRefreshBlocked(false);
    setCubeRefreshAttempt(0);
  }, [operationId]);
  useEffect(() => {
    onGenerationStatusChangeRef.current?.(
      generationStatus,
      generationStatus === 'DRAFT_CONFLICT' ? retryCubeRefresh : undefined,
      operationId,
    );
  }, [generationStatus, operationId, retryCubeRefresh]);
  useEffect(() => () => onGenerationStatusChangeRef.current?.(null), [cubeId]);
  useEffect(() => {
    if (operationBusy) setOpen(false);
  }, [operationBusy]);
  const valid = layers.some(l => l.id === source) && selectedDestinations.length > 0
    && selectedDestinations.every(id => destinations.some(l => l.id === id && canSelectDestination(l, source)));
  const error = generate.error as { response?: { data?: { message?: string } } } | null;
  const showDialog = () => {
    generate.reset();
    setShowHistory(false);
    const previous = info.data?.operation;
    const sourceId = layers.find(l => l.id === previous?.source_layer_id)?.id || '';
    setSource(sourceId);
    setSelectedDestinations((previous?.destination_layer_ids || []).filter(id =>
      destinations.some(l => l.id === id && canSelectDestination(l, sourceId))));
    setOpen(true);
  };
  const changeSource = (sourceId: string) => {
    setSource(sourceId);
    setSelectedDestinations(selected => selected.filter(id =>
      destinations.some(l => l.id === id && canSelectDestination(l, sourceId))));
  };
  const startGeneration = () => {
    setOpen(false);
    setOperationId(null);
    setReconcilingStart(false);
    generationStartBaselineOperationIdRef.current = info.data?.operation?.operation_id || null;
    generationStartInfoUpdatedAtRef.current = info.dataUpdatedAt;
    reconciliationChecksRef.current = 0;
    generate.mutate();
  };
  const closeDialog = () => { if (!generate.isLoading) setOpen(false); };
  const decisionPointDisabled = info.isLoading || busy || disabled;
  const decisionPointTooltip = disabled
    ? disabledReason || 'Decision Point unavailable'
    : operationBusy
      ? generationStatusMessage[generationStatus!]
      : info.isLoading
        ? 'Loading Decision Point…'
        : 'Decision Point';
  if (info.data && !layers.some(l => l.type === 'BUDGET')) return null;
  return <>
    <Tooltip title={decisionPointTooltip}>
      <span
        tabIndex={decisionPointDisabled ? 0 : undefined}
        role={decisionPointDisabled ? 'button' : undefined}
        aria-disabled={decisionPointDisabled || undefined}
        aria-label={decisionPointDisabled ? `Decision Point: ${decisionPointTooltip}` : undefined}
      >
        <IconButton
          aria-label='Decision Point'
          aria-haspopup='dialog'
          aria-expanded={open}
          disabled={decisionPointDisabled}
          onClick={showDialog}
          sx={{
            padding: 0,
            '& .MuiSvgIcon-root': {
              transition: 'transform 350ms cubic-bezier(0.34, 1.56, 0.64, 1), filter 200ms ease',
            },
            '&:hover .MuiSvgIcon-root': {
              transform: 'scale(1.15)',
              filter: 'drop-shadow(0 0 6px rgba(66, 187, 147, 0.7)) drop-shadow(0 0 14px rgba(66, 187, 147, 0.4))',
            },
            '&:active .MuiSvgIcon-root': { transform: 'scale(0.94)' },
          }}
        >
          <NewReleasesIcon fontSize='medium' sx={{ color: '#42BB93' }} />
        </IconButton>
      </span>
    </Tooltip>
    <Modal open={open} onClose={closeDialog}>
      <Stack
        sx={{ ...modalContentStyle, width: showHistory ? 960 : modalContentStyle.width }}
        spacing={2}
        role='dialog'
        aria-modal='true'
        aria-labelledby='budget-control-title'
        tabIndex={-1}
      >
        <Stack direction='row' justifyContent='space-between' alignItems='center' spacing={1}>
          <Stack direction='row' alignItems='center' spacing={1.25}>
            <NewReleasesIcon sx={{ color: '#42BB93' }} />
            <Stack>
              <Typography id='budget-control-title' variant='modal-title' component='h2'>
                {showHistory ? 'Generation history' : 'Generate Budget Control Framework'}
              </Typography>
              <Typography variant='caption' sx={{ color: '#98A2AE' }}>{info.data?.cubeName}</Typography>
            </Stack>
          </Stack>
          <IconButton size='small' aria-label='Close' disabled={generate.isLoading} onClick={closeDialog}>
            <HighlightOffRoundedIcon sx={{ color: '#98A2AE' }} />
          </IconButton>
        </Stack>
        <Divider />
        {showHistory ? <BudgetGenerationHistory cubeId={cubeId} /> : <Stack spacing={2}>
          {info.data && !info.data.canGenerate && <Typography variant='caption'>Only Support, Packager, and Tester users can generate the Budget Control Framework.</Typography>}
          {!busy && operation.data?.status === 'COMPLETED' && <Alert severity='success'>Budget Control Framework generated successfully.</Alert>}
          {!busy && currentOperation?.status === 'COMPLETED' && <Typography variant='caption'>
            Authorised by: {currentOperation.authorised_by_name && currentOperation.authorised_by_name !== currentOperation.authorised_by
              ? currentOperation.authorised_by_name : 'Unknown user'} · Date: {new Date(currentOperation.completed_at || currentOperation.requested_at).toLocaleString()}
          </Typography>}
          {(info.isError || operation.isError) && <Alert severity='error'>Could not load generation status. Refresh to try again.</Alert>}
          {!busy && status === 'FAILED' && <Alert severity='error'>Generation failed. Destination values were not replaced.</Alert>}

          {!busy && <>
          <Stack spacing={1.5} sx={sectionBoxStyle}>
            <Typography variant='body2' fontWeight={700} sx={{ color: '#3B4797' }}>Source</Typography>
            <Autocomplete
              size='small'
              fullWidth
              sx={filterAutoCompleteStyle}
              options={layers}
              value={layers.find(layer => layer.id === source) || null}
              getOptionLabel={layer => layer.name}
              isOptionEqualToValue={(option, value) => option.id === value.id}
              onChange={(_event, value) => changeSource(value?.id || '')}
              disabled={busy}
              renderInput={params => <TextField {...params} label='Source layer' />}
            />
          </Stack>
          <Stack spacing={1} sx={sectionBoxStyle}>
            <Typography id='budget-destination-label' variant='body2' fontWeight={700} sx={{ color: '#3B4797' }}>
              Destination layers
            </Typography>
            <FormGroup aria-labelledby='budget-destination-label'>
              {destinations.map(layer => (
                <Stack key={layer.id} direction='row' alignItems='center' justifyContent='space-between' spacing={1}>
                  <FormControlLabel
                    sx={{ m: 0, flex: 1, minWidth: 0, gap: 0.5, '& .MuiFormControlLabel-label': { fontSize: 14 } }}
                    label={layer.name}
                    control={<Checkbox
                      size='small'
                      sx={{
                        color: '#bfbfbf',
                        '&.Mui-checked': { color: '#42BB93' },
                        '&.Mui-disabled': { color: '#D0D5DD' },
                      }}
                      checked={selectedDestinations.includes(layer.id)}
                      disabled={busy || !canSelectDestination(layer, source)}
                      onChange={(_event, checked) => setSelectedDestinations(selected => checked
                        ? [...selected, layer.id] : selected.filter(id => id !== layer.id))}
                    />}
                  />
                  <Typography variant='caption' sx={{ color: '#98A2AE', flexShrink: 0 }}>
                    {layer.id === source ? 'Source layer' : layer.type === 'BUDGET' ? 'Budget' : 'General'}
                  </Typography>
                </Stack>
              ))}
            </FormGroup>
          </Stack>
          {layers.find(l => l.id === source)?.type === 'BUDGET' && <Typography variant='caption'>
            A Budget source can be copied to General layers.
          </Typography>}
          </>}
          {generate.isError && <Alert severity='error'>{error?.response?.data?.message || 'Could not start generation. Please try again.'}</Alert>}
        </Stack>}
        <Divider />
        <Stack direction='row' spacing={1.5} justifyContent='space-between' alignItems='center'>
          <Button
            size='small'
            variant='main-table-panel-borderless'
            startIcon={showHistory ? <ArrowBackIcon /> : <HistoryOutlinedIcon />}
            sx={{ textTransform: 'none', fontSize: 12, minWidth: 0, px: 1 }}
            disabled={generate.isLoading || operationBusy}
            onClick={() => setShowHistory(value => !value)}
          >
            {showHistory ? 'Back' : 'History'}
          </Button>
          <Stack direction='row' spacing={1.5}>
          <Button variant='main-table-panel-border' sx={{ textTransform: 'none' }} disabled={generate.isLoading} onClick={closeDialog}>
            {showHistory || operation.data?.status === 'COMPLETED' || status === 'FAILED' ? 'Close' : 'Cancel'}
          </Button>
          {!showHistory && <ButtonLoading
            variant='main-table-panel'
            sx={{ textTransform: 'none' }}
            aria-label='Generate Budget Control Framework'
            loading={generate.isLoading}
            disabled={!info.data?.canGenerate || !valid || busy || disabled}
            onClick={startGeneration}
          >
            Generate
          </ButtonLoading>}
          </Stack>
        </Stack>
      </Stack>
    </Modal>
  </>;
}
